import type { LevelData } from '@/game/level';
import type { Selection } from './editor-store';

export interface Issue {
  message: string;
  jumpTo: Selection;
}

export interface ValidationResult {
  blocking: Issue[];
  warnings: Issue[];
}

export function validate(lvl: LevelData): ValidationResult {
  const blocking: Issue[] = [];
  const warnings: Issue[] = [];

  if (!lvl.meta.name || lvl.meta.name.trim() === '') {
    blocking.push({ message: 'Level name is required', jumpTo: { kind: 'meta' } });
  }
  if (!lvl.meta.difficulty) {
    blocking.push({ message: 'Difficulty is required', jumpTo: { kind: 'meta' } });
  }
  if (lvl.segments.length === 0) {
    blocking.push({ message: 'Level must have at least one segment', jumpTo: { kind: 'settings' } });
  }

  let hasSpikes = false;
  for (const seg of lvl.segments) {
    if (seg.length <= 0) {
      blocking.push({ message: `Segment "${seg.id}" has length ≤ 0`, jumpTo: { kind: 'segment', segmentId: seg.id } });
    }
    for (let i = 0; i < seg.obstacles.length; i++) {
      const o = seg.obstacles[i];
      if (o.type === 'spike') hasSpikes = true;
      if (o.position.up < -5) {
        warnings.push({
          message: `Obstacle is below floor (up = ${o.position.up})`,
          jumpTo: { kind: 'obstacle', segmentId: seg.id, index: i },
        });
      }
      if (o.position.along < 0 || o.position.along > seg.length) {
        warnings.push({
          message: `Obstacle outside segment range (along = ${o.position.along})`,
          jumpTo: { kind: 'obstacle', segmentId: seg.id, index: i },
        });
      }
      if (o.type === 'moving' && !o.behavior) {
        blocking.push({
          message: `Moving obstacle at along=${o.position.along} has no behavior`,
          jumpTo: { kind: 'obstacle', segmentId: seg.id, index: i },
        });
      }
      if (o.type === 'jumppad') {
        const padSize = o.visual?.padSize;
        if (padSize !== undefined && padSize !== 'small' && padSize !== 'medium' && padSize !== 'large') {
          warnings.push({
            message: `Jump pad has invalid padSize "${padSize}" (expected small | medium | large)`,
            jumpTo: { kind: 'obstacle', segmentId: seg.id, index: i },
          });
        }
      }
      if (o.type === 'orb') {
        const orbSize = o.visual?.orbSize;
        if (orbSize !== undefined && orbSize !== 'small' && orbSize !== 'medium' && orbSize !== 'large') {
          warnings.push({
            message: `Orb has invalid orbSize "${orbSize}" (expected small | medium | large)`,
            jumpTo: { kind: 'obstacle', segmentId: seg.id, index: i },
          });
        }
        if (o.position.up <= 0) {
          warnings.push({
            message: `Orb at along=${o.position.along} is on the ground (up=${o.position.up}); orbs are typically floated mid-air`,
            jumpTo: { kind: 'obstacle', segmentId: seg.id, index: i },
          });
        }
      }
    }

    if (seg.mode === 'complex') {
      if (seg.terrain.type !== 'tunnel') {
        warnings.push({
          message: `Complex segment "${seg.id}" should use tunnel terrain`,
          jumpTo: { kind: 'terrain', segmentId: seg.id },
        });
      }
    }

    // Ceiling-floor overlap warning
    if (seg.terrain.ceiling && seg.terrain.floor) {
      const floorMax = (seg.terrain.floor.offset ?? 0) + (seg.terrain.floor.amplitude ?? 0);
      const ceilingMin = (seg.terrain.ceiling.offset ?? 0) - (seg.terrain.ceiling.amplitude ?? 0);
      if (ceilingMin <= floorMax + 40) {
        warnings.push({
          message: `Segment "${seg.id}" — ceiling is too close to floor (player may not fit)`,
          jumpTo: { kind: 'terrain', segmentId: seg.id },
        });
      }
    }
  }

  const totalLength = lvl.segments.reduce((s, seg) => s + seg.length, 0);
  for (const t of (lvl.triggers ?? [])) {
    if (t.position < 0 || t.position > totalLength) {
      warnings.push({
        message: `Trigger "${t.id}" position (${t.position}) is outside the level range (0..${totalLength})`,
        jumpTo: { kind: 'trigger', id: t.id },
      });
    }
  }

  for (const t of (lvl.triggers ?? [])) {
    if (t.type === 'mode') {
      const target = (t.params as { targetMode?: string }).targetMode;
      if (target && !lvl.segments.some((s) => s.mode === target)) {
        warnings.push({
          message: `Mode trigger targets "${target}" but no segment uses that mode`,
          jumpTo: { kind: 'trigger', id: t.id },
        });
      }
    }
  }

  if (hasSpikes && lvl.meta.optimalInputs === 0) {
    warnings.push({
      message: 'optimalInputs is 0 but level has spikes — elegance scoring will be off',
      jumpTo: { kind: 'meta' },
    });
  }

  return { blocking, warnings };
}
